import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "components/Card";
import Button from "components/Button";
import { getTemplateAPI, getTemplateQuestionsAPI } from "api/apiHelper";
import { Template, Question } from "types/template-builder-types";
import { MdArrowBack, MdEdit } from "react-icons/md";

const ViewTemplate = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [template, setTemplate] = useState<Template | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);

  useEffect(() => {
    if (templateId) {
      fetchTemplateData();
    }
  }, [templateId]);

  const fetchTemplateData = async () => {
    try {
      setIsLoading(true);
      const templateResponse = await getTemplateAPI(Number(templateId));
      const questionsResponse = await getTemplateQuestionsAPI(Number(templateId));
      
      setTemplate(templateResponse.data);
      setQuestions(questionsResponse.data || []);
    } catch (error: any) {
      console.error("Error fetching template:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to load template";
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotalWeightage = () => {
    return questions.reduce((sum, q) => sum + Number(q.weightage), 0);
  };

  if (isLoading) {
    return (
      <div className="mt-5 flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading template...</p>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="mt-5 flex items-center justify-center py-12">
        <p className="text-red-500 dark:text-red-400">Template not found</p>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <div className="mb-6 flex items-center justify-between">
        <Button
          varient="secondary"
          onClick={() => navigate("/admin-appraisals")}
          className="flex items-center gap-2 px-4 py-2"
        >
          <MdArrowBack className="h-5 w-5" />
          Back
        </Button>
        <Button
          varient="primary"
          onClick={() => navigate(`/admin-appraisals/templates/${templateId}/edit`)}
          className="flex items-center gap-2 px-6 py-2"
        >
          <MdEdit className="h-5 w-5" />
          Edit Template
        </Button>
      </div>

      <Card extra="p-6 mb-6">
        <div className="space-y-4">
          <div>
            <h2 className="text-2xl font-bold text-navy-700 dark:text-white mb-2">
              {template.name}
            </h2>
            <div className="flex items-center gap-3">
              <span
                className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  template.is_active
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                {template.is_active ? "Active" : "Inactive"}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Created: {new Date(template.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          
          {template.description && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">
                Description
              </h3>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {template.description}
              </p>
            </div>
          )}

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Questions
                </p>
                <p className="text-2xl font-bold text-navy-700 dark:text-white">
                  {questions.length}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Weightage
                </p>
                <p className="text-2xl font-bold text-navy-700 dark:text-white">
                  {calculateTotalWeightage().toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="mb-4">
        <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
          Questions ({questions.length})
        </h3>
      </div>

      {questions.length === 0 ? (
        <Card extra="p-8">
          <p className="text-center text-gray-500 dark:text-gray-400">
            No questions added to this template yet.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {questions.map((question, index) => (
            <Card
              key={question.id}
              extra="p-6 bg-gray-50 dark:bg-navy-700/30"
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                        Question # {index + 1}
                      </span>
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        {Number(question.weightage).toFixed(1)}% Weightage
                      </span>
                    </div>
                    <p className="text-base font-medium text-navy-700 dark:text-white">
                      {question.question_text}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                  <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">
                    Rating Options
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    {(["1", "2", "3", "4", "5"] as const).map((key) => (
                      <div
                        key={key}
                        className="bg-white dark:bg-navy-800 p-3 rounded border border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                            Score: {key}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {question.options[key] || "-"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ViewTemplate;
